import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaHardHat, FaHome, FaBuilding, FaTools, FaTruck, FaPaintRoller, FaArrowRight } from 'react-icons/fa';

const apiUrl = import.meta.env.VITE_API_URL;

const ServiceCard = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${apiUrl}/service/service-card/`);
        if (!response.ok) {
          throw new Error('Failed to fetch services');
        }
        const data = await response.json();
        setServices(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const getServiceIcon = (serviceType) => {
    const iconClass = "text-4xl mb-4 transition-all duration-300 group-hover:text-white";
    switch (serviceType.toLowerCase()) {
      case 'residential':
        return <FaHome className={`${iconClass} text-[#7bbf42]`} />;
      case 'commercial':
        return <FaBuilding className={`${iconClass} text-[#f9b414]`} />;
      case 'renovation':
        return <FaTools className={`${iconClass} text-[#7bbf42]`} />;
      case 'foundation':
        return <FaHardHat className={`${iconClass} text-[#f9b414]`} />;
      case 'remodeling':
        return <FaPaintRoller className={`${iconClass} text-[#7bbf42]`} />;
      default:
        return <FaTruck className={`${iconClass} text-[#f9b414]`} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-white">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 rounded-full border-4 border-[#7bbf42] border-t-transparent"
        ></motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-white">
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto"
        >
          {error}
        </motion.div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-[#7bbf42] opacity-5 rounded-full -translate-x-32 -translate-y-32"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#f9b414] opacity-5 rounded-full translate-x-48 translate-y-48"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Animated header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-[#040404] mb-4">
            Our <span className="relative inline-block">
              <span className="relative z-10">Construction Services</span>
              <span className="absolute bottom-0 left-0 w-full h-3 bg-[#f9b414] opacity-40 -z-0"></span>
            </span>
          </h2>
          <p className="text-xl text-[#040404] max-w-3xl mx-auto opacity-90">
            Comprehensive solutions tailored to your <span className="font-semibold text-[#7bbf42]">construction needs</span> with <span className="font-semibold text-[#f9b414]">unmatched quality</span>
          </p>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              onHoverStart={() => setHoveredCard(service.id)}
              onHoverEnd={() => setHoveredCard(null)}
              className={`group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-500 ${hoveredCard && hoveredCard !== service.id ? 'opacity-80' : 'opacity-100'}`}
            >
              {/* Card background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-gray-50 z-0"></div>
              
              {/* Animated overlay */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                  scale: hoveredCard === service.id ? 1 : 0.8, 
                  opacity: hoveredCard === service.id ? 1 : 0 
                }}
                className="absolute inset-0 bg-gradient-to-br from-[#7bbf42] to-[#f9b414] z-10 transition-all duration-500"
              ></motion.div>
              
              {/* Card content */}
              <div className="relative z-20 p-8 h-full flex flex-col">
                {getServiceIcon(service.type)}
                
                <h3 className="text-2xl font-bold text-[#040404] mb-3 group-hover:text-white transition-colors duration-300">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 mb-6 group-hover:text-white transition-colors duration-300">
                  {service.shortDescription}
                </p>
                
                <div className="mt-auto">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: hoveredCard === service.id ? '100%' : 0 }}
                    className="h-0.5 bg-white mb-4 transition-all duration-500"
                  ></motion.div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-[#040404] group-hover:text-white transition-colors duration-300">
                      {service.startingPrice ? `From $${service.startingPrice}` : 'Get a Quote'}
                    </span>
             
                  </div>
                </div>
              </div>
              
              {/* Floating features */}
              <div className="absolute bottom-4 left-4 z-20">
                <div className="flex flex-wrap gap-2">
                  {service.keyFeatures.slice(0, 3).map((feature, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ 
                        opacity: hoveredCard === service.id ? 1 : 0,
                        y: hoveredCard === service.id ? 0 : 10
                      }}
                      transition={{ duration: 0.3, delay: idx * 0.1 }}
                      className="inline-block px-3 py-1 bg-white bg-opacity-90 text-[#040404] text-xs font-medium rounded-full shadow-sm"
                    >
                      {feature}
                    </motion.span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>


   
      </div>
    </section>
  );
};

export default ServiceCard;